'use client';

import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { DataAnalytics } from '@/types/analytics';
import { DataTest } from '@/types/data-test';
import { clsx } from 'clsx';
import Link from 'next/link';
import { MouseEvent } from 'react';

type CinemarkLinkPropsBase = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children: React.ReactNode;
  href: string;
  primary?: boolean;
  useAnchor?: boolean;
} & DataTest;

type CinemarkLinkPropsTracking = CinemarkLinkPropsBase & DataAnalytics & {
  disableAutoTracking?: false;
};

type CinemarkLinkPropsNoTracking = CinemarkLinkPropsBase & Partial<DataAnalytics> & {
  disableAutoTracking: true;
};

type CinemarkLinkProps = CinemarkLinkPropsTracking | CinemarkLinkPropsNoTracking;

export const CinemarkLink = ({
  children,
  className,
  dataLinkCategory,
  dataLinkName,
  dataTest,
  disableAutoTracking,
  href,
  primary,
  useAnchor,
  ...props
}: CinemarkLinkProps): React.JSX.Element => {
  const { trackClick } = useDataAnalytics();

  const { onClick, ...restProps } = props;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    trackClick({
      linkCategory: dataLinkCategory,
      linkName: dataLinkName,
    });
    onClick?.(event);
  };

  const onClickProp = disableAutoTracking ? onClick : handleClick;

  if (useAnchor) {
    return (
      <a className={clsx(className, primary && 'text-primary font-bold hover:text-primary-darker')}
        data-test={dataTest}
        href={href}
        onClick={onClickProp}
        {...restProps}
      >
        {children}
      </a>
    );
  }

  return (
    <Link className={clsx(className, primary && 'text-primary font-bold hover:text-primary-darker')}
      data-test={dataTest}
      href={href}
      onClick={onClickProp}
      {...restProps}
    >
      {children}
    </Link>
  );
};
